import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { fetchAll } from '@/lib/paginate'
import { useHousehold } from '@/context/HouseholdContext'
import { accountBalance, cardFaturaSigned, endOfMonthISO, type FinanceTx } from '@/lib/finance'
import type { Account, ExchangeRate } from '@/types/db'

export function useAccounts(includeArchived = false) {
  const { householdId } = useHousehold()
  return useQuery({
    queryKey: ['accounts', householdId, includeArchived],
    queryFn: async () => {
      let q = supabase.from('accounts').select('*').eq('household_id', householdId).order('name')
      if (!includeArchived) q = q.eq('archived', false)
      const { data, error } = await q
      if (error) throw error
      return data as Account[]
    },
  })
}

export type AccountInput = Pick<Account, 'name' | 'type' | 'currency' | 'initial_balance'> & {
  archived?: boolean
  group_name?: string | null
  icon?: string | null
}

export function useSaveAccount() {
  const { householdId } = useHousehold()
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, values }: { id?: string; values: AccountInput }) => {
      if (id) {
        const { error } = await supabase.from('accounts').update(values).eq('id', id)
        if (error) throw error
      } else {
        const { error } = await supabase.from('accounts').insert({ ...values, household_id: householdId })
        if (error) throw error
      }
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['accounts'] })
      void qc.invalidateQueries({ queryKey: ['balances'] })
      void qc.invalidateQueries({ queryKey: ['cardMonthFatura'] })
      void qc.invalidateQueries({ queryKey: ['cardTotalDebt'] })
    },
  })
}

// Conta os lançamentos da conta por status (para decidir excluir x arquivar).
export async function fetchAccountTxCounts(accountId: string): Promise<{ paid: number; pending: number }> {
  const [{ count: paid }, { count: pending }] = await Promise.all([
    supabase
      .from('transactions')
      .select('id', { count: 'exact', head: true })
      .eq('account_id', accountId)
      .eq('status', 'paid'),
    supabase
      .from('transactions')
      .select('id', { count: 'exact', head: true })
      .eq('account_id', accountId)
      .eq('status', 'pending'),
  ])
  return { paid: paid ?? 0, pending: pending ?? 0 }
}

// Exclui a conta. Só deve ser chamada quando NÃO há lançamentos efetivados
// (pagos): remove primeiro os pendentes e depois a conta. Lançamentos pagos
// impediriam a exclusão (FK on delete restrict) — nesse caso, arquivar.
export function useDeleteAccount() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (id: string) => {
      const { error: pErr } = await supabase
        .from('transactions')
        .delete()
        .eq('account_id', id)
        .eq('status', 'pending')
      if (pErr) throw pErr
      const { error } = await supabase.from('accounts').delete().eq('id', id)
      if (error) throw error
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['accounts'] })
      void qc.invalidateQueries({ queryKey: ['balances'] })
      void qc.invalidateQueries({ queryKey: ['cardMonthFatura'] })
      void qc.invalidateQueries({ queryKey: ['cardTotalDebt'] })
      void qc.invalidateQueries({ queryKey: ['transactions'] })
    },
  })
}

// Arquiva a conta (histórico preservado; some das listas ativas).
export function useArchiveAccount() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, archived = true }: { id: string; archived?: boolean }) => {
      const { error } = await supabase.from('accounts').update({ archived }).eq('id', id)
      if (error) throw error
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['accounts'] })
      void qc.invalidateQueries({ queryKey: ['balances'] })
      void qc.invalidateQueries({ queryKey: ['cardMonthFatura'] })
      void qc.invalidateQueries({ queryKey: ['cardTotalDebt'] })
    },
  })
}

// Dias do cartão (1..31; null = não definido):
//  - due_day     → vencimento (quando você paga)
//  - closing_day → fechamento (compra depois dele vai para a fatura seguinte)
// Mudar o fechamento re-agrupa as faturas ⇒ invalida também os totais de fatura.
export function useUpdateCardDays() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, patch }: { id: string; patch: Partial<Pick<Account, 'due_day' | 'closing_day'>> }) => {
      const { error } = await supabase.from('accounts').update(patch).eq('id', id)
      if (error) throw error
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['accounts'] })
      void qc.invalidateQueries({ queryKey: ['cardMonthFatura'] })
    },
  })
}

// Saldo por conta = saldo inicial + entradas - saídas (na moeda da conta).
// Conta APENAS transações com status 'paid' (o saldo atual reflete o que já
// aconteceu; pendentes entram só no "saldo previsto" do dashboard).
export interface AccountBalance {
  account: Account
  balance: number
}

export function useBalances(includeArchived = false) {
  const { householdId, baseCurrency } = useHousehold()
  return useQuery({
    queryKey: ['balances', householdId, includeArchived, baseCurrency],
    queryFn: async (): Promise<AccountBalance[]> => {
      let accQuery = supabase.from('accounts').select('*').eq('household_id', householdId).order('name')
      if (!includeArchived) accQuery = accQuery.eq('archived', false)
      const [{ data: accounts, error: aerr }, txs, { data: rateRows }] = await Promise.all([
        accQuery,
        // Pagina TODAS as transações pagas (o cap de 1000 do PostgREST inflava saldos).
        fetchAll<{
          account_id: string
          kind: string
          amount: number
          status: string
          currency: string
          amount_account: number | null
        }>((from, to) =>
          supabase
            .from('transactions')
            .select('account_id, kind, amount, status, currency, amount_account')
            .eq('household_id', householdId)
            .eq('status', 'paid')
            .range(from, to),
        ),
        // taxas atuais (X -> base) p/ converter transações em moeda ≠ da conta
        supabase
          .from('exchange_rates')
          .select('base, quote, date, rate')
          .eq('quote', baseCurrency)
          .order('date', { ascending: false }),
      ])
      if (aerr) throw aerr
      const rates = latestRates(rateRows as ExchangeRate[] | null, baseCurrency)
      const all = (txs ?? []) as FinanceTx[]
      return (accounts as Account[]).map((account) => {
        const mine = all.filter((t) => t.account_id === account.id)
        return { account, balance: accountBalance(account, mine, rates, { onlyPaid: true }) }
      })
    },
  })
}

// Fatura do mês por cartão. Retorna Map<cartaoId, valor COM SINAL> — positivo =
// dívida, negativo = crédito a favor (não descarta o crédito).
//
// `byStatement` escolhe a régua (ver finance.ts):
//   false (padrão) → por DATA: a DÍVIDA de verdade. Dashboard/patrimônio usam esta.
//   true           → por FATURA: o que é COBRADO nesta fatura. Telas de fatura.
//
// O filtro `date <= fim do mês` no servidor é seguro para as duas: uma compra só
// pode ser empurrada para uma fatura POSTERIOR (o form impede escolher uma anterior
// à data), então fatura ≥ data e nada que caberia no recorte é deixado de fora.
export function useCardMonthFatura(year?: number, month0?: number, byStatement = false) {
  const { householdId, baseCurrency } = useHousehold()
  const now = new Date()
  const y = year ?? now.getFullYear()
  const m = month0 ?? now.getMonth()
  const ym = `${y}-${m}`
  return useQuery({
    queryKey: ['cardMonthFatura', householdId, ym, byStatement, baseCurrency],
    queryFn: async (): Promise<Map<string, number>> => {
      const endOfMonth = endOfMonthISO(y, m)
      const [{ data: accounts, error: aerr }, txs, { data: rateRows }] = await Promise.all([
        supabase
          .from('accounts')
          .select('*')
          .eq('household_id', householdId)
          .eq('type', 'cartao')
          .eq('archived', false),
        fetchAll<FinanceTx>((from, to) =>
          supabase
            .from('transactions')
            .select('account_id, kind, amount, currency, amount_account, date, statement_month')
            .eq('household_id', householdId)
            .lte('date', endOfMonth)
            .range(from, to),
        ),
        supabase
          .from('exchange_rates')
          .select('base, quote, date, rate')
          .eq('quote', baseCurrency)
          .order('date', { ascending: false }),
      ])
      if (aerr) throw aerr
      const rates = latestRates(rateRows as ExchangeRate[] | null, baseCurrency)
      const all = txs ?? []
      const map = new Map<string, number>()
      for (const account of accounts as Account[]) {
        const mine = all.filter((t) => t.account_id === account.id)
        map.set(account.id, cardFaturaSigned(account, mine, endOfMonth, rates, { byStatement }))
      }
      return map
    },
  })
}

// Saldo ALL-STATUS por cartão (todos os status, TODAS as datas — inclui compras
// futuras/parcelas lançadas). Negativo = dívida; positivo = crédito. Usado no
// PATRIMÔNIO (dívida real total, regra "lançado = dívida real").
export function useCardTotalDebt() {
  const { householdId, baseCurrency } = useHousehold()
  return useQuery({
    queryKey: ['cardTotalDebt', householdId, baseCurrency],
    queryFn: async (): Promise<Map<string, number>> => {
      const [{ data: accounts, error: aerr }, txs, { data: rateRows }] = await Promise.all([
        supabase.from('accounts').select('*').eq('household_id', householdId).eq('type', 'cartao').eq('archived', false),
        fetchAll<{ account_id: string; kind: string; amount: number; currency: string; amount_account: number | null }>(
          (from, to) =>
            supabase
              .from('transactions')
              .select('account_id, kind, amount, currency, amount_account')
              .eq('household_id', householdId)
              .range(from, to),
        ),
        supabase
          .from('exchange_rates')
          .select('base, quote, date, rate')
          .eq('quote', baseCurrency)
          .order('date', { ascending: false }),
      ])
      if (aerr) throw aerr
      const rates = latestRates(rateRows as ExchangeRate[] | null, baseCurrency)
      const all = (txs ?? []) as FinanceTx[]
      const map = new Map<string, number>()
      for (const account of accounts as Account[]) {
        const mine = all.filter((t) => t.account_id === account.id)
        // saldo all-status com sinal (neg=dívida, pos=crédito) via accountBalance
        map.set(account.id, accountBalance(account, mine, rates, { onlyPaid: false }))
      }
      return map
    },
  })
}

// Mapa moeda -> taxa (para a base) pegando a cotação mais recente de cada moeda.
export function latestRates(rows: ExchangeRate[] | null, baseCurrency: string): Map<string, number> {
  const sorted = [...(rows ?? [])].sort((a, b) => (a.date < b.date ? 1 : -1))
  const latest = new Map<string, number>()
  for (const r of sorted) if (!latest.has(r.base)) latest.set(r.base, Number(r.rate))
  latest.set(baseCurrency, 1)
  return latest
}
